import firebase from '../components/firebase.js';
import {
  addNotificationsDeprecated,
  addNotificationsForMentionsIfDoesntAlreadyExist,
} from './notifications';
import { toggleUpvote } from './upvotes';

export const postRef = (postId) => firebase.database().ref('posts/' + postId);
export const postsRef = () => firebase.database().ref('posts');

export const createPost = ({
  content,
  replyToId = null,
  successCallback,
  uid,
  room,
  modality = null,
}) => {
  if (!room) {
    console.error('createPost must receive value for room');
    return;
  }

  const post = {
    content,
    timestamp: firebase.database.ServerValue.TIMESTAMP,
    userId: uid,
    replyToId,
    room,
    ...(modality &&
      !replyToId && {
        modalities: {
          [modality]: {
            name: modality,
            votes: { [uid]: true },
          },
        },
      }),
  };

  const postId = postsRef().push().key;
  return postsRef()
    .child(postId)
    .update(post)
    .then(() => toggleUpvote({ postId, uid }))
    .then(() => {
      if (replyToId) {
        return addNotificationsDeprecated({ postId: replyToId, uid });
      }
    })
    .then(() => {
      addNotificationsForMentionsIfDoesntAlreadyExist({
        postContent: content,
        postId,
        myUserId: uid,
      });
    })
    .then(() => {
      if (successCallback && typeof successCallback === 'function') {
        successCallback();
      }
      return postId;
    });
};

export const deletePost = ({ postId }) => {
  const postRef = postsRef().child(postId);
  postsRef()
    .orderByChild('replyToId')
    .equalTo(postId)
    .once('value', (snapshot) => {
      const replies = snapshot.val();
      if (!replies) {
        postRef.remove();
      } else {
        postRef.update({ deleted: true });
      }
      // decrementNotifications({ postId: replyToId, myUserId }); // TODO
    });
};

export const editPost = ({
  id,
  content,
  uid,
  successCallback,
  modality = null, //TODO handle modality edits??
}) => {
  postRef(id)
    .update({
      content,
    })
    .then(() => {
      addNotificationsForMentionsIfDoesntAlreadyExist({
        postContent: content,
        postId: id,
        myUserId: uid,
      });
    })
    .then(() => {
      successCallback();
    });
};

// Global posts cache
export const GLOBAL_CACHE = {
  posts: null,
  lastUpdated: null,
  CACHE_DURATION: 5 * 60 * 1000, // 5 minutes
};

export const getPosts = (paramsOrCallback, callback) => {
  // Handle old calling pattern (just callback)
  if (typeof paramsOrCallback === 'function') {
    callback = paramsOrCallback;
    paramsOrCallback = {};
  }

  const { roomId, userFeedUid, userIsPremium } = paramsOrCallback;
  const now = Date.now();
  const cacheIsStale =
    !GLOBAL_CACHE.lastUpdated ||
    now - GLOBAL_CACHE.lastUpdated > GLOBAL_CACHE.CACHE_DURATION;

  // If we have cache and it's not stale, use it
  if (GLOBAL_CACHE.posts && !cacheIsStale) {
    if (roomId && roomId !== 'home') {
      // Filter cached posts by room
      const filteredPosts = Object.entries(GLOBAL_CACHE.posts).reduce(
        (acc, [id, post]) => {
          if (post.room === roomId) {
            acc[id] = { ...post, id };
          }
          return acc;
        },
        {}
      );
      callback(filteredPosts);
    } else {
      callback(GLOBAL_CACHE.posts);
    }
    return;
  }

  // If no cache or stale, fetch from Firebase
  const firebaseDatabaseRefPosts = firebase.database().ref('posts');
  let postsRef = null;

  // For home page or user feed, get ALL posts
  if (userFeedUid || !roomId || roomId === 'home') {
    postsRef = firebaseDatabaseRefPosts;
  } else if (!userIsPremium) {
    postsRef = firebaseDatabaseRefPosts.orderByChild('room').equalTo('general');
  } else if (roomId) {
    postsRef = firebaseDatabaseRefPosts.orderByChild('room').equalTo(roomId);
  }

  postsRef.once('value', (postsSnapshot) => {
    let posts = postsSnapshot.val() || {};

    // Add IDs to all posts
    posts = Object.entries(posts).reduce((acc, [id, post]) => {
      acc[id] = { ...post, id };
      return acc;
    }, {});

    // Update global cache with ALL posts
    GLOBAL_CACHE.posts = posts;
    GLOBAL_CACHE.lastUpdated = now;

    // Return filtered posts if needed
    if (roomId && roomId !== 'home') {
      const filteredPosts = Object.entries(posts).reduce((acc, [id, post]) => {
        if (post.room === roomId) {
          acc[id] = post;
        }
        return acc;
      }, {});
      callback(filteredPosts);
    } else {
      callback(posts);
    }
  });
};

const getModalityPostsArrayForUser = ({ uid }, callback) => {
  getPosts((posts) => {
    const modalityPosts = Object.values(posts).filter(
      (post) => post.userId === uid && post.modalities
    );
    callback(modalityPosts);
  });
};

export const getMostRecentModalityPostTimestampForUser = (
  { uid },
  callback
) => {
  getModalityPostsArrayForUser({ uid }, (modalityPostsArray) => {
    const mostRecentModalityPostTimestamp =
      modalityPostsArray.length > 0 &&
      Math.max.apply(
        Math,
        modalityPostsArray.map((post) => post.timestamp)
      );
    callback(mostRecentModalityPostTimestamp);
  });
};

export const addTag = ({ postId, content, successCallback, uid }) => {
  const key = postRef(postId).child('tags').push().key;
  postRef(postId)
    .child('tags')
    .child(key)
    .update({
      type: content,
      userId: uid,
    })
    .then(successCallback());
};

//TODO refactor someday
export const removeTag = ({ postId, content, uid }) => {
  postRef(postId)
    .child('tags')
    .once('value', (snapshot) => {
      const tags = snapshot.val();
      const tag =
        tags &&
        typeof tags === 'object' &&
        Object.entries(tags).filter(
          ([key, tag]) => tag.userId === uid && tag.type === content
        );
      if (tag && tag.length === 1) {
        const tagKey = tag[0][0];
        postRef(postId).child('tags').child(tagKey).remove();
      }
    });
};

export const setModalityVote = ({ postId, vote, uid, modalityName }) => {
  postsRef()
    .child(postId)
    .child('modality')
    .once('value', (snapshot) => {
      const modality = snapshot.val();
      if (modality && modality.name === modalityName) {
        postRef(postId).child('modality').child('votes').child(uid).set(vote);
      } else {
        console.error(
          'setModalityVote reached where modalityName does not exist as this post modality'
        );
      }
    });
  postsRef()
    .child(postId)
    .child('modalities')
    .once('value', (snapshot) => {
      const modalities = snapshot.val();
      if (modalities && modalities[modalityName]) {
        postRef(postId)
          .child('modalities')
          .child(modalityName)
          .child('votes')
          .child(uid)
          .set(vote);
      } else {
        console.error(
          'setModalityVote reached where modalityName does not exist on this post modalities'
        );
      }
    });
};

export const getModalityVotes = ({ postId, modalityName }) => {
  return postRef(postId)
    .child('modalities')
    .child(modalityName)
    .child('votes')
    .once('value')
    .then((snapshot) => snapshot.val() || {});
};

export const getModalityStats = ({ postId, modalityName }) => {
  return getModalityVotes({ postId, modalityName }).then((votes) => {
    const voteValues = Object.values(votes);
    return {
      total: voteValues.length,
      positive: voteValues.filter((v) => v.value === true).length,
      negative: voteValues.filter((v) => v.value === false).length,
    };
  });
};
